<script setup lang="ts">
import type { Form, FormErrorEvent, FormSubmitEvent } from '#ui/types';
import { phoneValidator } from '@lib/shared';
import { z } from 'zod';

import {
    type BackendErrorEntity,
    type ErrorType,
    type UpdateAccountDto,
    useApiCompleteOnboardingSession,
    useApiGetPaymentMethods,
    useApiUpdateAccountPersonalData,
} from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';

// COMPOSABLES
const { logError, logInfo } = useLogs();
const { tt } = useTypedI18n();
const { notifyBackendError, notifyGenericError } = useNotification();
const { getFormErrors } = useBackendErrors();
const { data: session, getSession } = useAuthUtils();
const { mutateAsync: mutateUpdatePersonalData, isPending: isPendingPersonalData } = useApiUpdateAccountPersonalData();
const { data: paymentMethods, isFetching: isFetchingPaymentMethods } = useApiGetPaymentMethods();
const { mutateAsync: mutateCompleteOnboarding, isPending: isPendingCompleteOnboarding } = useApiCompleteOnboardingSession();

// EVENTS
const emit = defineEmits(['next']);

// DATA
const formSchema = z.object({
    firstname: z.string(),
    lastname: z.string(),
    phone: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.phone') })).refine(value => !value || phoneValidator(value), {
        message: tt('validation.msg.phone'),
    }),
});
type FormSchema = z.infer<typeof formSchema>;
const formData = reactive<FormSchema>({
    firstname: session.value?.user?.firstname ?? '',
    lastname: session.value?.user?.lastname ?? '',
    phone: session.value?.user?.phone ?? '',
});
const form = ref<Form<any>>();

const isSubmitting = computed(() => isPendingPersonalData.value || isPendingCompleteOnboarding.value);

// METHODS
const completeOnboarding = async (): Promise<void> => {
    await mutateCompleteOnboarding()
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error during signup onboarding salesPrediction step: user or company id undefined', {
                apiError,
                userId: session.value?.user?.id,
            });

            if (!apiError.data?.error) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({ error });
        })
        .then(() => getSession());
};

const onSubmit = (event: FormSubmitEvent<FormSchema>): void => {
    const { data } = event;

    const updateData: UpdateAccountDto = {
        firstname: data.firstname || null,
        lastname: data.lastname || null,
        phone: data.phone || null,
    };

    mutateUpdatePersonalData({ data: updateData })
        .then(() => {
            completeOnboarding().then(() => emit('next'));
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error updating account personal data', { error: apiError });

            if (!apiError.data?.error) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({ error });
            const formErrors = getFormErrors<UpdateAccountDto, FormSchema>(apiError, {
                firstname: 'firstname',
                lastname: 'lastname',
                phone: 'phone',
            });
            form.value?.setErrors(formErrors);
            scrollToFormError();
        });
};

const onError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before signup step company', {
        formErrors: errorEvent.errors,
        formData,
    });

    if (!errorEvent.errors.length) return;

    scrollToFormError();
};
</script>

<template>
    <UForm ref="form" class="flex flex-col gap-4" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onError">
        <p class="text-center">{{ tt('signup.onboarding.contact.subtitle') }}</p>

        <div class="flex gap-4">
            <UFormField class="w-full" :label="tt('signup.fields.firstname')" name="firstname">
                <UInput v-model.trim="formData.firstname" :disabled="isSubmitting" :autocomplete="AUTOCOMPLETE_OFF" />
            </UFormField>

            <UFormField class="w-full" :label="tt('signup.fields.lastname')" name="lastname">
                <UInput v-model.trim="formData.lastname" :disabled="isSubmitting" :autocomplete="AUTOCOMPLETE_OFF" />
            </UFormField>
        </div>

        <UFormField :label="tt('signup.fields.phone')" name="phone" required>
            <template #hint>
                <BasePhoneHint />
            </template>
            <BasePhoneInput v-model.trim="formData.phone" :disabled="isSubmitting" :country-code="session?.user?.company?.countryCode" />
        </UFormField>

        <USeparator />

        <USkeleton v-if="isFetchingPaymentMethods" class="h-9 w-full" />
        <PaymentMethodsAddModalButton v-else-if="!paymentMethods?.length">
            <template #label>
                {{ tt('paymentMethods.addNewPaymentMethod') }}
            </template>
        </PaymentMethodsAddModalButton>
        <div v-else>
            <PaymentMethodsCard v-for="pm in paymentMethods" :key="pm.id" :payment-method="pm" />
        </div>

        <UButton :label="tt('signup.onboarding.confirm')" class="mt-4" type="submit" size="xl" :loading="isSubmitting" block />
    </UForm>
</template>
